import { fetchJson } from "@/services/api";
import type { ApiClassroom, ApiClassroomSchedule } from "@/types";
import type { ClassData } from "@/types";

interface ClassroomsApiResponse {
  message: string;
  data: ApiClassroom[];
}

interface PaginatedClassroomsApiResponse {
  message: string;
  data: ApiClassroom[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

interface ClassroomDetailApiResponse {
  message: string;
  data: ApiClassroom;
}

const CYCLE_LEVEL_COLOR: Record<string, string> = {
  primaire: "bg-indigo-50 text-indigo-600",
  college: "bg-fuchsia-50 text-fuchsia-600",
  lycee: "bg-emerald-50 text-emerald-600",
};

const DAY_FR_SHORT: Record<string, string> = {
  monday: "Lun.",
  tuesday: "Mar.",
  wednesday: "Mer.",
  thursday: "Jeu.",
  friday: "Ven.",
  saturday: "Sam.",
  sunday: "Dim.",
};

const DAY_FR_FULL: Record<string, string> = {
  monday: "Lundi",
  tuesday: "Mardi",
  wednesday: "Mercredi",
  thursday: "Jeudi",
  friday: "Vendredi",
  saturday: "Samedi",
  sunday: "Dimanche",
};

function formatSchedule(schedules: ApiClassroomSchedule[]): string {
  return schedules
    .map((s) => `${DAY_FR_SHORT[s.day] ?? s.day} ${s.start_time} - ${s.end_time}`)
    .join(" • ");
}

function formatSessionDates(dates: { formatted: string }[]): string {
  return dates.map((d) => d.formatted).join(" / ");
}

function mapApiClassroomToClassData(api: ApiClassroom): ClassData {
  const studentsList = api.students?.map((s) => ({
    id: s.id,
    studentCode: s.student_code,
    firstName: s.first_name,
    lastName: s.last_name,
    email: s.email,
    enrollmentStatus: s.enrollment_status,
  }));

  const category: "soutien" | "stage" = api.type === "stage" ? "stage" : "soutien";

  const schedule =
    api.schedule_short ||
    (api.schedules?.length ? formatSchedule(api.schedules) : "") ||
    (api.session_dates?.length ? formatSessionDates(api.session_dates) : "") ||
    "--";

  return {
    id: String(api.id),
    name: api.name,
    levels: api.levels.map((l) => ({
      id: l.id,
      name: l.name,
      shortName: l.short_name,
      color: l.color,
      schoolCycle: l.school_cycle,
    })),
    level: api.levels[0]?.name ?? "--",
    levelCode: api.levels[0]?.short_name ?? api.levels[0]?.name ?? "--",
    schoolCycle: api.levels[0]?.school_cycle,
    levelColor:
      CYCLE_LEVEL_COLOR[api.levels[0]?.school_cycle ?? ""] ?? "bg-indigo-50 text-indigo-600",
    subject: api.subjects?.[0]?.name ?? "--",
    subLabel: api.is_active ? "Publié" : "Désactivé",
    price: parseFloat(api.base_price_monthly),
    teachers: api.primary_teacher
      ? [
          {
            id: String(api.primary_teacher.id),
            name: api.primary_teacher.name,
            role: "Prof",
            initials: api.primary_teacher.name
              .split(" ")
              .map((w) => w[0])
              .join(""),
            avatarColor: "bg-indigo-100",
          },
        ]
      : [],
    primary_teacher: api.primary_teacher
      ? {
          id: String(api.primary_teacher.id),
          user_id: api.primary_teacher.user_id ?? api.primary_teacher.id,
          name: api.primary_teacher.name,
          email: api.primary_teacher.email,
        }
      : undefined,
    students: api.enrolled_students_count ?? studentsList?.length ?? 0,
    studentsList,
    maxStudents: api.capacity,
    schedule,
    scheduleSlots: api.schedules?.map((s) => ({
      day: DAY_FR_FULL[s.day] ?? s.day,
      startTime: s.start_time,
      endTime: s.end_time,
    })),
    isInvisible: api.is_archived,
    waitingList: api.waitlist_count ?? 0,
    status: api.is_full ? "Complète" : "Ouverte",
    category,
    zones: api.zones,
    stageDates: api.session_dates?.map((d) => d.formatted),
    startDate: api.start_date ?? "",
    endDate: api.end_date ?? "",
    offer: api.offer
      ? {
          id: api.offer.id,
          name: api.offer.name,
          offer_code: api.offer.offer_code,
        }
      : undefined,
  };
}

export async function getClasses(schoolCycle?: string): Promise<ClassData[]> {
  const params = schoolCycle ? `?school_cycle=${schoolCycle}` : "";
  const res = await fetchJson<ClassroomsApiResponse>(`/v1/classrooms${params}`);
  return res.data.map(mapApiClassroomToClassData);
}

export interface ClassesQueryParams {
  page: number;
  perPage: number;
  archived?: boolean;
  /** "soutien" | "stage" — l'onglet d'offre. */
  type?: "soutien" | "stage";
  schoolCycle?: string;
  /** short_name du niveau (ex. "6e"). */
  level?: string;
  /** Nom exact de la matière. */
  subject?: string;
  search?: string;
  offerId?: number;
}

export interface PaginatedClasses {
  data: ClassData[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

export async function getClassesPaginated(
  params: ClassesQueryParams,
  signal?: AbortSignal,
): Promise<PaginatedClasses> {
  const qs = new URLSearchParams();
  qs.set("page", String(params.page));
  qs.set("per_page", String(params.perPage));
  if (params.archived !== undefined) qs.set("archived", params.archived ? "1" : "0");
  if (params.type) qs.set("type", params.type);
  if (params.schoolCycle) qs.set("school_cycle", params.schoolCycle);
  if (params.level) qs.set("level", params.level);
  if (params.subject) qs.set("subject", params.subject);
  if (params.search) qs.set("search", params.search);
  if (params.offerId !== undefined) qs.set("offer_id", String(params.offerId));

  const res = await fetchJson<PaginatedClassroomsApiResponse>(`/v1/classrooms?${qs.toString()}`, {
    signal,
  });
  return {
    data: res.data.map(mapApiClassroomToClassData),
    meta: res.meta,
  };
}

export async function getClassById(id: string): Promise<ApiClassroom | undefined> {
  const res = await fetchJson<ClassroomDetailApiResponse>(`/v1/classrooms/${id}`);
  return res.data;
}

export async function archiveClassroom(id: string): Promise<void> {
  await fetchJson<unknown>(`/v1/classrooms/${id}/archive`, {
    method: "PATCH",
    body: JSON.stringify({ id: Number(id) }),
  });
}

export async function unarchiveClassroom(id: string): Promise<void> {
  await fetchJson<unknown>(`/v1/classrooms/${id}/unarchive`, {
    method: "PATCH",
    body: JSON.stringify({ id: Number(id) }),
  });
}

export async function deleteClassroom(id: string): Promise<{ message: string }> {
  return fetchJson<{ message: string }>(`/v1/classrooms/${id}`, {
    method: "DELETE",
  });
}

export interface CreateClassroomPayload {
  educational_offer_id?: number;
  name?: string;
  description?: string;
  level_ids: number[];
  subject_ids: number[];
  base_price_monthly: number;
  capacity: number;
  start_date?: string;
  end_date?: string;
  start_from_class_start?: boolean;
  recurrence: string;
  schedules?: { day: string; start_time: string; end_time: string }[];
  session_dates?: { date: string; start_time: string; end_time: string }[];
  zone_ids?: number[];
  is_active: boolean;
  teacher_ids: number[];
}

export async function createClassroom(payload: CreateClassroomPayload): Promise<ApiClassroom> {
  const res = await fetchJson<ClassroomDetailApiResponse>("/v1/classrooms", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.data;
}

export interface UpdateClassroomPayload {
  educational_offer_id?: number;
  name?: string;
  description?: string;
  level_ids?: number[];
  subject_ids?: number[];
  base_price_monthly?: number;
  capacity?: number;
  start_date?: string;
  end_date?: string;
  start_from_class_start?: boolean;
  recurrence?: string;
  schedules?: { day: string; start_time: string; end_time: string }[];
  session_dates?: { date: string; start_time: string; end_time: string }[];
  zone_ids?: number[];
  is_active?: boolean;
  teacher_ids?: number[];
  zones?: string[];
}

export interface AssignInterimPayload {
  interim_teacher_profile_id: number;
  replaced_teacher_profile_id?: number | null;
  starts_at?: string | null;
  ends_at?: string | null;
  reason?: string | null;
}

export async function assignInterimTeacher(
  id: string,
  payload: AssignInterimPayload,
): Promise<void> {
  await fetchJson<unknown>(`/v1/classrooms/${id}/teachers/interim`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function removeInterimTeacher(
  classroomId: string,
  teacherProfileId: number,
): Promise<void> {
  await fetchJson<void>(`/v1/classrooms/${classroomId}/teachers/${teacherProfileId}/interim`, {
    method: "DELETE",
  });
}

export async function updateClassroomTeachers(id: string, teacherIds: number[]): Promise<void> {
  await fetchJson<unknown>(`/v1/classrooms/${id}/teachers`, {
    method: "PATCH",
    body: JSON.stringify({ teacher_ids: teacherIds }),
  });
}

export async function updateClassroom(
  id: string,
  payload: UpdateClassroomPayload,
): Promise<ApiClassroom> {
  const res = await fetchJson<ClassroomDetailApiResponse>(`/v1/classrooms/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return res.data;
}

export async function assignXpPoints(
  classroomUuid: string,
  studentId: number,
  xpPoints: number,
): Promise<void> {
  await fetchJson<void>(`/v1/teacher/classrooms/${classroomUuid}/students/${studentId}/xp`, {
    method: "POST",
    body: JSON.stringify({ xp_points: xpPoints }),
  });
}

export async function launchLiveSession(
  classroomUuid: string,
  sessionDateId: number,
): Promise<any> {
  return fetchJson<any>(
    `/v1/teacher/classrooms/${classroomUuid}/live-sessions/${sessionDateId}/start`,
    { method: "POST" },
  );
}

export interface ClassroomListItem {
  id: number;
  uuid: string;
  name: string;
  classroom_code: string;
  conversation_id: number | null;
  description?: string | null;
  is_active: boolean;
  is_archived: boolean;
  primary_teacher?: {
    id: number;
    user_id?: number | null;
    name: string | null;
    email: string | null;
  } | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export async function getClassroomsList(signal?: AbortSignal): Promise<ClassroomListItem[]> {
  const res = await fetchJson<{ data: ClassroomListItem[] }>("/v1/classrooms/list", { signal });
  return res.data;
}
